"use client";

import { useCallback, useMemo } from "react";
import type {
  ListingIntentFilter,
  SpecFilter,
} from "./listing-browse-types";
import type {
  useListingFilterState,
  useListingViewState,
} from "./listing-browse-state";

interface ActionDeps {
  filters: ReturnType<typeof useListingFilterState>;
  view: ReturnType<typeof useListingViewState>;
}

function countActiveFilters(filters: ActionDeps["filters"]): number {
  let count = 0;
  if (filters.searchQuery.length > 0) count++;
  if (filters.conditions.length > 0) count++;
  if (filters.priceRange.min !== null || filters.priceRange.max !== null) count++;
  if (filters.stateCode !== null) count++;
  if (filters.listedAfterDays !== null) count++;
  if (filters.intentFilter !== "all") count++;
  return count + filters.specFilters.length;
}

function useSelectIntentFilter(filters: ActionDeps["filters"]) {
  const { setIntentFilter, setPriceRange } = filters;
  return useCallback(
    (intent: ListingIntentFilter) => {
      setIntentFilter(intent);
      if (intent === "recycle") setPriceRange({ min: null, max: null });
    },
    [setIntentFilter, setPriceRange],
  );
}

export function useListingBrowseActions({ filters, view }: ActionDeps) {
  const { setSpecFilters } = filters;
  const { setMapVisible, setAdvancedFiltersExpanded } = view;

  const toggleMap = useCallback(
    () => setMapVisible((previous) => !previous),
    [setMapVisible],
  );

  const toggleAdvancedFilters = useCallback(
    () => setAdvancedFiltersExpanded((previous) => !previous),
    [setAdvancedFiltersExpanded],
  );

  const addSpecFilter = useCallback(
    (filter: SpecFilter) =>
      setSpecFilters((previous) => [
        ...previous.filter((entry) => entry.field !== filter.field),
        filter,
      ]),
    [setSpecFilters],
  );

  const removeSpecFilter = useCallback(
    (field: string) =>
      setSpecFilters((previous) =>
        previous.filter((entry) => entry.field !== field),
      ),
    [setSpecFilters],
  );

  const selectIntentFilter = useSelectIntentFilter(filters);
  const clearAllFilters = useClearAllFilters(filters);
  const activeFilterCount = useMemo(
    () => countActiveFilters(filters),
    [filters],
  );

  return {
    selectIntentFilter,
    toggleMap,
    toggleAdvancedFilters,
    addSpecFilter,
    removeSpecFilter,
    clearAllFilters,
    activeFilterCount,
    hasActiveFilters: activeFilterCount > 0,
  };
}

function useClearAllFilters(filters: ActionDeps["filters"]) {
  const {
    setConditions,
    setPriceRange,
    setSpecFilters,
    setStateCode,
    setListedAfterDays,
    setIntentFilter,
    setSearchQuery,
  } = filters;
  return useCallback(() => {
    setConditions([]);
    setPriceRange({ min: null, max: null });
    setSpecFilters([]);
    setStateCode(null);
    setListedAfterDays(null);
    setIntentFilter("all");
    setSearchQuery("");
  }, [
    setConditions,
    setPriceRange,
    setSpecFilters,
    setStateCode,
    setListedAfterDays,
    setIntentFilter,
    setSearchQuery,
  ]);
}
